'use client';

import React from 'react';

import { useParams, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';

import { BrowseProductsList } from '../BrowseProductsList';
import { Header } from '../Header';

import { Breadcrumbs } from '@/components/Breadcrumbs';
import { Filters } from '@/components/Filters';
import { BrowseByTypes } from '@/modules/activityTracker/constants';
import { useBrandCategoryPageMetadata } from '@/modules/brandCategory/useBrandCategoryPageMetadata';
import { useBrands } from '@/modules/brands/hooks/useBrands';
import { useBrandCategoryBreadcrumbs } from '@/modules/breadcrumbs/useBrandCategoryBreadcrumbs';
import { getPageTitle } from '@/modules/browse/helpers';
import { useBreadcrumbTrackingOnClick } from '@/modules/browse/hooks';
import { useBrowseTracking } from '@/modules/browse/useBrowseTracking';
import { LEGACY_DEFAULT_CMS_CATEGORY_PAGE } from '@/modules/cms/constants';
import { getCMSLocale } from '@/modules/cms/helpers/getCMSLocale';
import { useSearchId } from '@/modules/search/useSearchId';

export function BrandCategory() {
  const t = useTranslations('common');
  const { category, slug: brand } = useParams<{
    category: string[];
    slug: string;
  }>();
  const params = useSearchParams();
  const categoryPathname = (category as string[]).join('/');
  const cmsLocale = getCMSLocale();
  const searchId = useSearchId();

  const { data: brandCategoryPageMetadata } = useBrandCategoryPageMetadata(
    brand,
    categoryPathname
  );

  const { data: brands, isLoading: isBrandsLoading } = useBrands();
  const brandsList = brands?.data || {};
  const brandData = Object.values(brandsList).find(
    (brandById) => brandById.slug === brand
  );

  const breadcrumbItems = useBrandCategoryBreadcrumbs({
    brand: brandData,
    categoryPathname,
    enabled: !isBrandsLoading,
  });
  const onBreadcrumbClick = useBreadcrumbTrackingOnClick();
  const { copy: description } =
    brandCategoryPageMetadata || LEGACY_DEFAULT_CMS_CATEGORY_PAGE;

  const title = getPageTitle({
    pageMetadata: brandCategoryPageMetadata,
    categories: category,
    brand: brandData,
    includeBrand: true,
    locale: cmsLocale,
  });

  const { handleProductClick } = useBrowseTracking({
    searchId,
    browseType: BrowseByTypes.BRAND_CATEGORY,
    label: title,
  });

  return (
    <>
      <Breadcrumbs
        items={breadcrumbItems}
        onBreadcrumbClick={onBreadcrumbClick}
      />
      <Header
        title={title}
        description={description || t('ShopCollection.Default')}
      />
      <Filters />
      <BrowseProductsList
        handleProductClick={handleProductClick}
        query={params.get('q') || ''}
      />
    </>
  );
}
